<template>
  <transition name="search-nav-slide-down">
    <div
      v-if="shouldShowSearch"
      ref="search"
      class="additional-nav__block site-search"
      @mouseenter="$store.dispatch('nav/setAdditionalNav', 'search')"
    >
      <div
        class="site-search__inner-container"
        :class="[
          { 'site-search__inner-container--open': showInnerContainer },
          `site-search__inner-container--${sizeVariant}-input`,
        ]"
      >
        <span
          class="icon-cross site-search__icon-cross"
          @click="$store.dispatch('nav/setAdditionalNav', '')"
        />
        <input
          type="text"
          class="site-search__input"
          :class="[`site-search__input--${sizeVariant}`]"
          :placeholder="$t('site_search.search')"
          @input="handleInputChange"
          @focus="userIsScrolling = false"
          v-model="term"
          name="search_input"
          ref="search_input"
          autocomplete="new-password"
        />
        <div class="site-search__border"></div>
        <div class="site-search__wrapper">
          <LoadingIndicator v-if="isLoading" class="site-search__loader" />
          <!-- No results -->
          <div v-if="hasNoFilterPageItems" class="site-search__no-results">
            <p
              class="site-search__no-results-description"
              v-html="$t('site_search.no_results_msg', { term })"
            ></p>
            <div class="site-search__no-results-button-group">
              <button class="site-search__no-results-button" @click="followPageLink('shoe-finder')">
                {{ $t('global.go_to_shoe_finder') }}
              </button>
              <button class="site-search__no-results-button" @click="followPageLink('explore')">
                {{ $t('site_search.shop_all_products') }}
              </button>
            </div>
          </div>
          <!-- Search Intro -->
          <div v-if="introIsActive" class="site-search__intro">
            <span class="site-search__intro-header">{{ $t('site_search.recent_searches') }}:</span>
            <ul>
              <li class="site-search__intro-item" @click="insertRecommendedTerm('Cloudswift')">
                Cloudswift
              </li>
              <li class="site-search__intro-item" @click="insertRecommendedTerm('Waterproof')">
                Waterproof
              </li>
              <li class="site-search__intro-item" @click="insertRecommendedTerm('Long-T')">
                Long-T
              </li>
            </ul>
          </div>
          <!-- Search Results -->
          <div v-show="hasFilterPageItems" class="site-search__products" ref="productsContainer">
            <div v-show="hasFilterPageItems" class="site-search__status-bar">
              <div class="site-search__status-bar-results">
                {{ filterPageItems.length }} {{ $t('site_search.results') }}
              </div>
              <a
                class="site-search__status-bar-link u-arrow-link"
                @click="followPageLink('explore')"
                >{{ $t('site_search.shop_all_products') }}</a
              >
            </div>
            <Cards
              :onCardClick="handleCardClick"
              :cards="filterPageItems"
              :summaryIsHidden="true"
            />
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import algoliasearch from 'algoliasearch'
import { mapGetters } from 'vuex'
import fetchFilterPageItems from './filter-fetch-data'
import Cards from '@/components/FilterPage/Products/Cards/Cards.vue'
import LoadingIndicator from '@/components/_shared/LoadingIndicator.vue'

const Debouncer = On.Debouncer()
const algoliaAppId = 'ML35QLWPOC'
const algoliaKey = 'bff229776989d153121333c90db826b1'

export default {
  name: 'Search',
  components: { Cards, LoadingIndicator },

  data() {
    return {
      term: '',
      searchIndex: null,
      lastSearch: '',
      filterPageItems: [],
      isLoading: false,
      userIsScrolling: false,
      showInnerContainer: false,
    }
  },

  computed: {
    ...mapGetters('nav', ['shouldShowSearch']),

    introIsActive() {
      return this.term.length === 0
    },

    hasFilterPageItems() {
      return this.hasSearchedForFilterPageItems && this.filterPageItems.length > 0
    },

    hasNoFilterPageItems() {
      return this.hasSearchedForFilterPageItems && this.filterPageItems.length === 0
    },

    hasSearchedForFilterPageItems() {
      return this.term.length > 0 && !this.isLoading
    },

    sizeVariant() {
      return this.userIsScrolling && this.hasFilterPageItems ? 'small' : 'large'
    },
  },

  watch: {
    introIsActive() {
      if (!this.introIsActive) On.destroyMobileScrollHelper(this.$refs.search)
    },

    shouldShowSearch() {
      if (this.shouldShowSearch) {
        window.addEventListener('touchstart', this.blurInputField)
        window.dispatchEvent(new CustomEvent('on:search-open'))
        this.$nextTick(() => {
          this.showInnerContainer = true
          this.$refs.search_input.focus()
          On.initMobileScrollHelper(this.$refs.search)
          On.initMobileScrollHelper(this.$refs.productsContainer)
        })
      } else {
        window.removeEventListener('touchstart', this.blurInputField)
        On.destroyMobileScrollHelper(this.$refs.productsContainer)
        this.showInnerContainer = false
      }
      this.term = ''
      this.isLoading = false
      this.filterPageItems = []
    },
  },

  created() {
    const algoliaIndex = `${this.$store.state.page.environment}_products`
    const client = algoliasearch(algoliaAppId, algoliaKey)
    this.searchIndex = client.initIndex(algoliaIndex)
  },

  methods: {
    handleInputChange() {
      this.term = this.$refs.search_input.value
      this.userIsScrolling = false
      if (this.term.length > 0 && this.lastSearch !== this.term) {
        this.filterPageItems = []
        this.lastSearch = this.term
        this.isLoading = true
        Debouncer.debounce({
          callback: this.searchForFilterPageItems,
          debounceTime: 400,
        })
      } else {
        this.isLoading = false
      }
    },

    searchForFilterPageItems() {
      this.searchIndex
        .search({
          query: this.term,
          restrictSearchableAttributes: [
            `translated_fields.${this.$store.state.page.language}`,
            'color',
            'gender',
            'product_type',
            'product_name_synonyms',
          ],
        })
        .then(data => {
          const filterPageIds = data.hits.map(d => d.filter_page_item_id)
          fetchFilterPageItems(filterPageIds.filter(obj => obj))
            .then(response => {
              this.filterPageItems = response
              this.isLoading = false
            })
            .catch(() => this.resetSearch())
        })
        .catch(() => this.resetSearch())
      if (this.term.length >= 3) On.Analytics.trackSiteSearch.query(this.term)
    },

    insertRecommendedTerm(term) {
      this.$refs.search_input.value = term
      this.handleInputChange()
    },

    followPageLink(page) {
      this.$store.dispatch('nav/setAdditionalNav', '')
      window.location.href = `/${this.$store.state.page.locale}/${page}`
    },

    hideSearch() {
      this.$store.dispatch('nav/setAdditionalNav', '')
    },

    resetSearch() {
      this.isLoading = false
      this.filterPageItems = []
    },

    blurInputField() {
      this.userIsScrolling = true
      this.$refs.search_input.blur()
    },

    handleCardClick(product, event) {
      const classList = event.target.classList
      const productString = `${product.groupName} || ${product.productType} || ${product.sku}`
      if (
        classList.contains('color-selector__color-box') ||
        classList.contains('color-selector__color')
      ) {
        On.Analytics.trackSiteSearch.colorClick(productString)
      } else if (classList.contains('quick-add__size')) {
        On.Analytics.trackSiteSearch.addToCartClick(productString)
      } else {
        On.Analytics.trackSiteSearch.linkClick(productString)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
@import './Search.scss';
</style>
